import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Box, Dialog, IconButton } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { DayPicker, Matcher } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import DateInput from './DateInput';
import DatePickerStyles from './DatePicker.styles';
import { isValid, parse } from 'date-fns';

import type { Theme } from '@mui/material';
import type { SxProps } from '@mui/system';
export type MaybeDate = Date | undefined;
type AnyDate = number | string | MaybeDate;

type TDatePicker = FC<{
  label?: string;
  selected?: AnyDate;
  disabledDays?: Matcher | Matcher[];
  onSelect?: (v: MaybeDate) => void;
  sx?: SxProps<Theme>;
  modalised?: boolean;
  required?: boolean;
  errorText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  hideClearButton?: boolean;
}>;

// TODO: SS we can replace this with the offical MUI date picker
const DatePicker: TDatePicker = ({
  label = '',
  selected,
  disabledDays,
  onSelect,
  sx,
  modalised = false,
  required,
  errorText,
  disabled,
  size = 'medium',
  hideClearButton = false,
}) => {
  const [pickerDate, _setPickerDate] = useState<MaybeDate>();

  // TODO: why do we need to do this? Surely the DayPicker will rerender when the value (pickerDate) changes?
  const pickerKey = pickerDate?.toString();

  const setPickerDate = useCallback(
    (date: MaybeDate) => {
      if (date?.getTime() == pickerDate?.getTime()) return;
      _setPickerDate(date);
      if (onSelect) {
        onSelect(date);
      }
      setShowCalendar(false);
    },
    [onSelect, pickerDate],
  );

  useEffect(() => {
    // parses the selected date which may be a string or a Date object into a Date object
    let parsedDate;

    if (selected instanceof Date) {
      parsedDate = selected;
    } else if (typeof selected == 'string') {
      parsedDate = parse(selected, 'dd/MM/yyyy', new Date());
      if (!isValid(parsedDate)) {
        parsedDate = parse(selected, 'yyyy-MM-dd', new Date());
      }
    }

    if (isValid(parsedDate)) {
      _setPickerDate(parsedDate);
    }
  }, [selected]);

  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const toggleCalendar = useCallback(() => setShowCalendar(!showCalendar), [showCalendar]);

  const dayPicker = (
    <DayPicker
      key={pickerKey}
      mode="single"
      initialFocus
      defaultMonth={pickerDate}
      selected={pickerDate}
      onSelect={setPickerDate}
      disabled={disabledDays}
    />
  );

  return (
    <Box sx={{ m: 0, ...sx }}>
      <DatePickerStyles>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', flexDirextion: 'row', alignItems: 'center' }}>
            <DateInput
              label={label}
              value={pickerDate}
              required={required}
              disabled={disabled}
              errorText={errorText}
              onChange={setPickerDate}
              size={size}
              hideClearButton={hideClearButton}
            />

            <IconButton name="calendar" disabled={disabled} color="secondary" onClick={toggleCalendar}>
              <CalendarMonthIcon />
            </IconButton>
          </Box>

          {modalised ? (
            <Dialog open={showCalendar} onClose={() => setShowCalendar(false)}>
              {dayPicker}
            </Dialog>
          ) : (
            dayPicker
          )}
        </Box>
      </DatePickerStyles>
    </Box>
  );
};

export default DatePicker;
